import React from "react";
import CruiseWizardPage from "@/pages/Cruise/CruiseWizardPage";
import {
    Button,
    ButtonType,
    Checkbox,
    Dropdown,
    DropdownOrderBy,
    DropdownRequiredType,
    DropdownVerticalAlign,
    Form,
    Inline,
    InlineType,
    JustifyContent, TextAreaInput,
    TextInput
} from "@reapptor-apps/reapptor-react-components";
import {NumberParsingResult, NumberUtility} from "@reapptor-apps/reapptor-toolkit";
import CruiseConstants from "@/helpers/CruiseConstants";
import Localizer from "@/localization/Localizer";

import styles from "./PassengersInfo.module.scss";

export interface IPassengersInfoProps {
}

interface IPassengersInfoState {
    applicableNumberOfChildren: number[];
}

export default class PassengersInfo extends CruiseWizardPage<IPassengersInfoProps, IPassengersInfoState> {

    state: IPassengersInfoState = {
        applicableNumberOfChildren: [],
    };

    private readonly _formRef: React.RefObject<Form> = React.createRef();

    private get applicableNumberOfChildren(): number[] {
        const basePossibleSelections: number[] = (this.wizard.groupSizeValue)
            ? Array.from({length: CruiseConstants.maxGroupSizeValue}, (_, index) => index + 1)
            : [];

        const applicableNumberOfChildren: number[] = basePossibleSelections.filter(item => item == this.wizard.groupSizeValue || item < this.wizard.groupSizeValue);

        return (!this.wizard.groupSizeValue)
            ? basePossibleSelections
            : applicableNumberOfChildren;
    }

    private isValidGroupSize(value: string): string | null {
        const result: NumberParsingResult = NumberUtility.parse(value, false);
        return (result.parsed && result.value >= CruiseConstants.minGroupSizeValue && result.value <= CruiseConstants.maxGroupSizeValue)
            ? null
            : "*";
    }

    public async setGroupSizeValueAsync(value: string): Promise<void> {
        const result: NumberParsingResult = NumberUtility.parse(value, false);
        if (result.parsed) {
            this.wizard.groupSizeValue = result.value;
            this.saveContext();
        }
        await this.reRenderAsync();
    }

    public async selectChildrenComingAsync(childrenComing: boolean): Promise<void> {
        this.wizard.childrenComing = childrenComing;

        if (!childrenComing) {
            this.wizard.numberOfComingChildren = null;
        }

        this.saveContext();

        await this.reRenderAsync();
    }

    public async selectNumberOfComingChildrenAsync(value: number | null): Promise<void> {
        this.wizard.numberOfComingChildren = value;
        this.saveContext();
        await this.reRenderAsync();
    }

    public async selectAccessibilityLimitationsAsync(accessibilityLimitations: boolean): Promise<void> {
        this.wizard.accessibilityLimitations = accessibilityLimitations;
        this.wizard.accessibilityLimitationsComment = null;
        this.saveContext();
        await this.reRenderAsync();
    }

    public async setAccessibilityLimitationsCommentAsync(comment: string | null): Promise<void> {
        this.wizard.accessibilityLimitationsComment = comment;
        this.saveContext();
        await this.reRenderAsync();
    }

    public async nextAsync(): Promise<void> {
        if (this._formRef.current) {
            const isValid: boolean = await this._formRef.current.validateAsync();
            if (isValid) {
                this.saveContext();
                await super.nextAsync();
            }
        }
    }

    public get canNext(): boolean {
        return (this.wizard.groupSizeValue >= CruiseConstants.minGroupSizeValue) && (this.wizard.groupSizeValue <= CruiseConstants.maxGroupSizeValue);
    }


    public renderButtons(): React.ReactNode {
        return (
            <React.Fragment>
                <Button block type={ButtonType.Dark} label={Localizer.cruiseWizardNext}
                        onClick={() => this.nextAsync()}/>

                <Button block type={ButtonType.Light} label={Localizer.cruiseWizardBack}
                        onClick={() => this.prevAsync()}/>
            </React.Fragment>
        )
    }

    public renderContent(): React.ReactNode {
        return (
            <Form ref={this._formRef} className={styles.passengersInfo}>

                <TextInput autoFocus
                           placeholder={Localizer.cruiseWizardEstimatedGroupSize}
                           value={this.wizard.groupSizeValue ? this.wizard.groupSizeValue.toString() : ""}
                           validators={[this.isValidGroupSize]}
                           onChange={async (sender: TextInput, value: string) => this.setGroupSizeValueAsync(value)}
                />

                <p>{Localizer.cruiseWizardAreChildrenComing}</p>

                <Inline className={styles.item}>
                    <Checkbox inline radio
                              id={"checkboxChildrenComing"}
                              inlineType={InlineType.Right}
                              className={styles.radio}
                              label={Localizer.genericYes}
                              title={Localizer.genericYes}
                              value={this.wizard.childrenComing}
                              onChange={() => this.selectChildrenComingAsync(true)}
                    />

                    <Checkbox inline radio
                              id={"checkboxChildrenNotComing"}
                              inlineType={InlineType.Right}
                              className={styles.radio}
                              label={Localizer.genericNo}
                              title={Localizer.genericNo}
                              value={!this.wizard.childrenComing}
                              onChange={() => this.selectChildrenComingAsync(false)}
                    />
                </Inline>

                <Dropdown className={styles.item}
                          maxWidth={170}
                          minWidth={170}
                          filterMinLength={10}
                          orderBy={DropdownOrderBy.None}
                          verticalAlign={DropdownVerticalAlign.Bottom}
                          disabled={(!this.wizard.childrenComing)}
                          required={(this.wizard.childrenComing)}
                          requiredType={DropdownRequiredType.AutoSelect}
                          nothingSelectedText={Localizer.genericQuantity}
                          items={this.applicableNumberOfChildren}
                          selectedItem={this.wizard.numberOfComingChildren}
                          onChange={(sender: Dropdown<number | null>, value: number | null) => this.selectNumberOfComingChildrenAsync(value)}
                />

                <p>{Localizer.cruiseWizardAnyAccessibilityLimitations}</p>

                <Inline justify={JustifyContent.Center} className={styles.item}>
                    <Checkbox inline radio
                              id={"checkboxAccessibilityLimitations"}
                              inlineType={InlineType.Right}
                              className={styles.radio}
                              label={Localizer.genericYes}
                              title={Localizer.genericYes}
                              value={this.wizard.accessibilityLimitations}
                              onChange={() => this.selectAccessibilityLimitationsAsync(true)}
                    />

                    <Checkbox inline radio
                              id={"checkboxNoAccessibilityLimitations"}
                              inlineType={InlineType.Right}
                              className={styles.radio}
                              label={Localizer.genericNo}
                              title={Localizer.genericNo}
                              value={!this.wizard.accessibilityLimitations}
                              onChange={() => this.selectAccessibilityLimitationsAsync(false)}
                    />
                </Inline>

                <TextAreaInput autoFocus
                               id={"accessibilityLimitationsComment"}
                               className={this.css(styles.commentHidden, (this.wizard.accessibilityLimitations) && styles.commentVisible)}
                               required={(this.wizard.accessibilityLimitations)}
                               maxLength={CruiseConstants.descriptionLength}
                               rows={3}
                               placeholder={Localizer.genericWriteHere}
                               value={this.wizard.accessibilityLimitationsComment}
                               onChange={async (sender: TextAreaInput, value: string) => this.setAccessibilityLimitationsCommentAsync(value)}
                />

            </Form>
        );
    }
}